import { useCallback, useEffect, useMemo, useState } from "react";
import adminApi from "../api/adminApi";
import { createTemplatePrompt } from "../utils/defaults";
import {
  moveArrayItem,
  prependArrayItem,
  removeArrayItem,
  updateArrayItem
} from "../utils/arrayEditors";

function getDefaultTemplatesConfig() {
  return {
    pageSize: 5,
    prompts: []
  };
}

export function useAdminTemplates({ adminToken, enabled }) {
  const [templatesConfig, setTemplatesConfig] = useState(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesSaving, setTemplatesSaving] = useState(false);
  const [templatesDirty, setTemplatesDirty] = useState(false);

  const load = useCallback(async (token = adminToken) => {
    if (!token) return;
    setTemplatesLoading(true);
    try {
      const res = await adminApi.getTemplatesConfig(token);
      setTemplatesConfig(res.data || null);
      setTemplatesDirty(false);
    } catch (error) {
      console.error("Ошибка загрузки шаблонов:", error);
    } finally {
      setTemplatesLoading(false);
    }
  }, [adminToken]);

  const save = useCallback(async () => {
    if (!adminToken || !templatesConfig) return;
    setTemplatesSaving(true);
    try {
      const res = await adminApi.saveTemplatesConfig(adminToken, templatesConfig);
      if (res.data?.config) {
        setTemplatesConfig(res.data.config);
      }
      setTemplatesDirty(false);
    } catch (error) {
      console.error("Ошибка сохранения шаблонов:", error);
      window.alert("Ошибка сохранения шаблонов");
    } finally {
      setTemplatesSaving(false);
    }
  }, [adminToken, templatesConfig]);

  useEffect(() => {
    if (!enabled || !adminToken) return;
    load(adminToken);
  }, [enabled, adminToken, load]);

  const updateConfig = useCallback((patch) => {
    setTemplatesConfig((prev) => (prev ? { ...prev, ...patch } : prev));
    setTemplatesDirty(true);
  }, []);

  const mutatePrompts = useCallback((updater) => {
    setTemplatesConfig((prev) => {
      const base = prev || getDefaultTemplatesConfig();
      const prompts = Array.isArray(base.prompts) ? base.prompts : [];
      return {
        ...base,
        prompts: updater(prompts)
      };
    });
    setTemplatesDirty(true);
  }, []);

  const prompts = useMemo(
    () => (Array.isArray(templatesConfig?.prompts) ? templatesConfig.prompts : []),
    [templatesConfig]
  );

  return {
    state: {
      templatesConfig,
      templatesLoading,
      templatesSaving,
      templatesDirty,
      prompts
    },
    actions: {
      reload: () => load(adminToken),
      save,
      updateConfig,
      updatePrompt: (index, patch) => mutatePrompts((list) => updateArrayItem(list, index, patch)),
      movePrompt: (index, direction) => mutatePrompts((list) => moveArrayItem(list, index, direction)),
      removePrompt: (index) => mutatePrompts((list) => removeArrayItem(list, index)),
      addPrompt: () => mutatePrompts((list) => prependArrayItem(list, createTemplatePrompt()))
    }
  };
}

export default useAdminTemplates;
